import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useCattle } from '@/contexts/CattleContext';
import { Package, Search, Filter, Eye, Image as ImageIcon, Grid, Table } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function CattleInventory() {
  const { cattle, getLatestWeight } = useCattle();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBreed, setFilterBreed] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'

  const breeds = ['all', ...new Set(cattle.map(c => c.breed))];

  const filteredCattle = cattle.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.tag.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBreed = filterBreed === 'all' || c.breed === filterBreed;
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesBreed && matchesStatus;
  });

  const calculateAge = (birthDate) => {
    if (!birthDate) return 'N/A';
    const birth = new Date(birthDate);
    const today = new Date();
    const months = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
    return `${Math.floor(months / 12)}a ${months % 12}m`;
  };

  /**
   * Calcula la categoría del animal según su edad y género
   * 
   * Rangos de edad para MACHOS:
   * - Ternero: 0-12 meses
   * - Novillo: 13-36 meses
   * - Adulto: 37+ meses
   * 
   * Rangos de edad para HEMBRAS:
   * - Ternera: 0-12 meses
   * - Vaquilla: 13-24 meses
   * - Vaca/Adulta: 25+ meses
   */
  const getAgeCategory = (birthDate, gender) => {
    if (!birthDate) return null;
    
    const birth = new Date(birthDate);
    const today = new Date();
    const ageInMonths = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());

    if (gender === 'Macho') {
      if (ageInMonths <= 12) return { label: 'Ternero', color: 'bg-blue-100 text-blue-700' };
      if (ageInMonths <= 36) return { label: 'Novillo', color: 'bg-cyan-100 text-cyan-700' };
      return { label: 'Adulto', color: 'bg-slate-100 text-slate-700' };
    } else if (gender === 'Hembra') {
      if (ageInMonths <= 12) return { label: 'Ternera', color: 'bg-pink-100 text-pink-700' };
      if (ageInMonths <= 24) return { label: 'Vaquilla', color: 'bg-rose-100 text-rose-700' };
      return { label: 'Vaca', color: 'bg-purple-100 text-purple-700' };
    }
    
    return null;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center shadow-md">
              <Package className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Inventario de Ganado</h1>
              <p className="text-muted-foreground">Vista completa de todos los animales registrados</p>
            </div>
          </div>
          
          {/* View Toggle */}
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'cards' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className={viewMode === 'cards' ? 'bg-rose-600 hover:bg-rose-700' : ''}
            >
              <Grid className="h-4 w-4 mr-2" />
              Cards
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('table')}
              className={viewMode === 'table' ? 'bg-rose-600 hover:bg-rose-700' : ''}
            >
              <Table className="h-4 w-4 mr-2" />
              Tabla
            </Button>
          </div>
        </div>
      </div>

      <Card className="mb-6 border-border/50 shadow-sm">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o tag..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterBreed} onValueChange={setFilterBreed}>
              <SelectTrigger>
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filtrar por raza" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las razas</SelectItem>
                {breeds.filter(b => b !== 'all').map(breed => (
                  <SelectItem key={breed} value={breed}>{breed}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="Activo">Activo</SelectItem>
                <SelectItem value="Vendido">Vendido</SelectItem>
                <SelectItem value="Enfermo">Enfermo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-border/50">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Total</p>
            <p className="text-2xl font-bold">{filteredCattle.length}</p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Activos</p>
            <p className="text-2xl font-bold text-success">
              {filteredCattle.filter(c => c.status === 'Activo').length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Machos</p>
            <p className="text-2xl font-bold">
              {filteredCattle.filter(c => c.gender === 'Macho').length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Hembras</p>
            <p className="text-2xl font-bold">
              {filteredCattle.filter(c => c.gender === 'Hembra').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {filteredCattle.length > 0 ? (
        <>
          {/* Cards View */}
          {viewMode === 'cards' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCattle.map((animal) => {
                const latestWeight = getLatestWeight(animal.id);
                const ageCategory = getAgeCategory(animal.birthDate, animal.gender);
                
                return (
                  <Card key={animal.id} className="overflow-hidden border-border/50 shadow-sm hover:shadow-md transition-all duration-300 group">
                <div className="relative h-48 bg-gradient-to-br from-muted/50 to-muted overflow-hidden">
                  {animal.imageUrl ? (
                    <img
                      src={animal.imageUrl}
                      alt={animal.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="h-16 w-16 text-muted-foreground/30" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3 flex gap-2">
                    {ageCategory && (
                      <Badge className={`shadow-sm text-xs ${ageCategory.color}`}>
                        {ageCategory.label}
                      </Badge>
                    )}
                    <Badge 
                      variant={animal.status === 'Activo' ? 'default' : 'secondary'}
                      className="shadow-sm"
                    >
                      {animal.status}
                    </Badge>
                  </div>
                </div>

                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <CardTitle className="text-xl group-hover:text-rose-600 transition-colors">
                          {animal.name}
                        </CardTitle>
                        {animal.hasRegistry && (
                          <Badge className="bg-amber-500 text-white text-xs h-5">
                            ⭐
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{animal.tag}</p>
                    </div>
                    <Link to={`/ganaderia/detalle/${animal.id}`}>
                      <Button size="icon" variant="ghost" className="hover:bg-rose-100">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs">Raza</p>
                        <p className="font-medium">{animal.breed}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Género</p>
                        <p className="font-medium">{animal.gender || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Edad</p>
                        <p className="font-medium">{calculateAge(animal.birthDate)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Último Peso</p>
                        <p className="font-medium text-rose-600">
                          {latestWeight ? `${latestWeight.weight} kg` : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {animal.location && (
                      <div className="pt-3 border-t">
                        <p className="text-xs text-muted-foreground">Ubicación</p>
                        <p className="text-sm font-medium">{animal.location}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
            </div>
          )}

          {/* Table View */}
          {viewMode === 'table' && (
            <Card className="border-border/50 shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/30 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Animal
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Número
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Categoría
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Edad
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Peso
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Ganadería Compra
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-border">
                    {filteredCattle.map((animal) => {
                      const latestWeight = getLatestWeight(animal.id);
                      const ageCategory = getAgeCategory(animal.birthDate, animal.gender);
                      
                      return (
                        <tr key={animal.id} className="hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-rose-100 to-rose-200 overflow-hidden flex items-center justify-center">
                                {animal.imageUrl ? (
                                  <img
                                    src={animal.imageUrl}
                                    alt={animal.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="text-sm font-bold text-rose-600">
                                    {animal.name.charAt(0)}
                                  </span>
                                )}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{animal.name}</div>
                                <div className="text-xs text-muted-foreground">{animal.breed}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{animal.tag}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {ageCategory ? (
                              <Badge className={`text-xs ${ageCategory.color}`}>
                                {ageCategory.label}
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">N/A</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{calculateAge(animal.birthDate)}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-rose-600">
                              {latestWeight ? `${latestWeight.weight} kg` : 'N/A'}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{animal.purchaseFarm || 'N/A'}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <Badge 
                              variant={animal.status === 'Activo' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {animal.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Link to={`/ganaderia/detalle/${animal.id}`}>
                              <Button size="icon" variant="ghost" className="hover:bg-rose-100">
                                <Eye className="h-4 w-4 text-rose-600" />
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      ) : (
        <Card className="border-border/50">
          <CardContent className="py-16 text-center">
            <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="text-lg font-semibold mb-2">No se encontraron resultados</h3>
            <p className="text-muted-foreground mb-4">Intente ajustar los filtros de búsqueda</p>
            <Button onClick={() => {
              setSearchTerm('');
              setFilterBreed('all');
              setFilterStatus('all');
            }}>
              Limpiar Filtros
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
